import type { Source } from "@types"
import { formatDate, formatNumber } from "@core/helpers"
import { buildOptimTurpeResultDetailProps } from "@modules/courtage/utils/buildOptimTurpeAnalysisDetail"
import {
    isOptimTurpeTemplateC5,
    isOptimTurpeTemplateC2,
} from "@modules/courtage/constants/optim-turpe"
import type {
    OptimTurpeAnalyse,
    OptimTurpeAnalyseResponse,
    OptimTurpeChart,
    OptimTurpeChartSubchart,
    OptimTurpeTemplate,
    OptimTurpeTemplateView,
} from "@modules/courtage/types/optim-turpe"

function getMetricValue(
    analyse: OptimTurpeAnalyse,
    key: string
): string | number | boolean | null | undefined {
    return analyse.data?.[key]?.value
}

/**
 * Valeur métier brute : l’API envoie souvent `str_value` sans `value` (ex. segment « C5 »).
 */
function getMetricScalar(
    analyse: OptimTurpeAnalyse,
    key: string
): string | number | boolean | null | undefined {
    const metric = analyse.data?.[key]
    if (!metric) return undefined
    if (metric.str_value != null && String(metric.str_value).trim() !== "") {
        return metric.str_value
    }
    return metric.value
}

function resolveTemplateForSegment(
    templates: OptimTurpeTemplate[] | undefined,
    templateName: string,
    segment: string
): OptimTurpeTemplate | undefined {
    if (!templates?.length) return undefined

    const exact = templates.find((item) => item.name === templateName)
    if (exact) return exact

    const loose = templates.find(
        (item) =>
            item.name?.includes(templateName.replace("optimturpe_", "")) ||
            item.name === templateName
    )
    if (loose) return loose

    const bySegment = templates.find((item) =>
        segment
            ? item.name?.toUpperCase().includes(segment.toUpperCase())
            : false
    )
    if (bySegment) return bySegment

    return templates.length === 1 ? templates[0] : undefined
}

/** True si la valeur décrit un bloc graphique (certains templates ont `charts.mandatory: true`). */
function isChartPayload(v: unknown): v is OptimTurpeChart {
    return typeof v === "object" && v !== null && !Array.isArray(v)
}

/**
 * Normalise les subcharts runtime (record API attendu, ou tableau observé en production)
 * vers un dictionnaire indexé par nom (fallback index) pour une fusion stable.
 */
function normalizeChartSubcharts(
    subcharts: unknown
): Record<string, OptimTurpeChartSubchart | undefined> {
    if (!subcharts || typeof subcharts !== "object") return {}

    if (Array.isArray(subcharts)) {
        return subcharts.reduce(
            (acc, item, index) => {
                if (!item || typeof item !== "object") return acc
                const subchart = item as OptimTurpeChartSubchart
                const baseKey =
                    subchart.name && String(subchart.name).trim() !== ""
                        ? String(subchart.name)
                        : String(index)
                const key = acc[baseKey] ? `${baseKey}-${index}` : baseKey
                acc[key] = subchart
                return acc
            },
            {} as Record<string, OptimTurpeChartSubchart | undefined>
        )
    }

    return subcharts as Record<string, OptimTurpeChartSubchart | undefined>
}

/**
 * Clés de graphique non affichées dans le widget (l’API / le template peuvent toujours les fournir).
 * Ex. consommation journalière : non souhaitée dans l’UI optimisation contractuelle.
 */
const EXCLUDED_OPTIM_TURPE_CHART_IDS = new Set(["historique_conso"])

function getMetricDisplay(
    analyse: OptimTurpeAnalyse,
    key: string,
    fallback = "-"
): string {
    const metric = analyse.data?.[key]
    if (!metric) return fallback
    if (metric.str_value != null && metric.str_value !== "") {
        return String(metric.str_value)
    }
    if (metric.value == null || metric.value === "") {
        return fallback
    }
    return String(metric.value)
}

/**
 * Fusionne la définition d’un graphique template + résultat d’analyse.
 * Important : fusion **par clé de subchart** — un spread naïf écrasait tout `subcharts`
 * avec la partie analyse et supprimait les traces du template non repris dans le payload,
 * ou l’inverse (template sans x/y + analyse avec une seule série).
 */
function mergeChartDefinition(
    templateChart: OptimTurpeChart | undefined,
    analyseChart: OptimTurpeChart | undefined,
    /** Clé du graphique dans `charts` (ex. `historique_puissance`) — sert d’identifiant UI si `name` absent. */
    chartKey: string
): OptimTurpeChart {
    const t = isChartPayload(templateChart) ? templateChart : {}
    const a = isChartPayload(analyseChart) ? analyseChart : {}

    const tSubs = normalizeChartSubcharts(t.subcharts)
    const aSubs = normalizeChartSubcharts(a.subcharts)
    const subchartKeys = new Set([...Object.keys(tSubs), ...Object.keys(aSubs)])

    const mergedSubcharts: Record<string, OptimTurpeChartSubchart> = {}
    for (const key of subchartKeys) {
        const tSub = tSubs[key] ?? {}
        const aSub = aSubs[key] ?? {}
        const tContent = (tSub as { content?: Record<string, unknown> }).content
        const aContent = (aSub as { content?: Record<string, unknown> }).content

        mergedSubcharts[key] = {
            ...tSub,
            ...aSub,
            content: {
                ...(tContent ?? {}),
                ...(aContent ?? {}),
            },
        } as OptimTurpeChartSubchart
    }

    const { subcharts: _ts, ...tRest } = t
    const { subcharts: _as, ...aRest } = a

    const analyseName = String((aRest as { name?: string }).name ?? "").trim()
    const templateName = String((tRest as { name?: string }).name ?? "").trim()
    const resolvedChartName = analyseName || templateName || chartKey

    return {
        ...tRest,
        ...aRest,
        ...(Object.keys(mergedSubcharts).length > 0
            ? { subcharts: mergedSubcharts }
            : {}),
        name: resolvedChartName,
    }
}

/**
 * Fusionne modèle (titres, sous-graphiques du template) et surcouches d’analyse par clé de chart.
 */
function mergeCharts(
    analyse: OptimTurpeAnalyse,
    template: OptimTurpeTemplate | undefined
): OptimTurpeChart[] {
    const analyseCharts = analyse.charts ?? {}
    const templateCharts = template?.charts ?? {}

    const templateKeyList = Object.keys(templateCharts)
    const analyseKeyList = Object.keys(analyseCharts)
    /** Ordre du template en premier (titres cohérents), puis clés uniquement présentes dans l’analyse. */
    const orderedKeys = [
        ...templateKeyList,
        ...analyseKeyList.filter((key) => !templateKeyList.includes(key)),
    ]

    const chartKeys = orderedKeys.filter(
        (key) =>
            !EXCLUDED_OPTIM_TURPE_CHART_IDS.has(key) &&
            (isChartPayload(analyseCharts[key]) ||
                isChartPayload(templateCharts[key]))
    )

    return chartKeys.map((key) =>
        mergeChartDefinition(templateCharts[key], analyseCharts[key], key)
    )
}

export function mapOptimAnalysesToTemplateView(
    sourceId: string,
    response: OptimTurpeAnalyseResponse | null,
    meter?: Source | null
): OptimTurpeTemplateView[] {
    if (!response?.sources?.[sourceId]?.length) {
        return []
    }

    /** Ordre API brut (homeys-front ne retrie pas après filtre SUCCESS). */
    const analyses = response.sources[sourceId].filter(
        (item) => item.status === "SUCCESS"
    )

    return analyses.map((analyse) => {
        const segment = String(getMetricScalar(analyse, "segment") ?? "")
        const templateName = isOptimTurpeTemplateC5(segment)
            ? "optimturpe_C5"
            : isOptimTurpeTemplateC2(segment)
              ? "optimturpe_C2"
              : "optimturpe_C4"

        const template = resolveTemplateForSegment(
            response.templates,
            templateName,
            segment
        )
        const economies = getMetricValue(
            analyse,
            "economies_positives_avec_cta"
        )

        const displayName = String(analyse.name_analyse ?? "").trim() ?? ""

        return {
            _id: analyse._id,
            name: displayName,
            createdAt: analyse.createdAt ?? analyse.updatedAt,
            url: analyse.url_rapport,
            expiredDate: analyse.date_expiration_rapport,
            segment,
            analysisData: analyse.data,
            comment: String(getMetricScalar(analyse, "comment") ?? ""),
            economiesTotal:
                typeof economies === "number"
                    ? formatNumber(economies, { style: "currency" })
                    : getMetricDisplay(analyse, "economies_positives_avec_cta"),
            kpis: [
                {
                    label: "FTA actuelle",
                    value: getMetricDisplay(analyse, "fta_avant"),
                },
                {
                    label: "FTA optimale",
                    value: getMetricDisplay(analyse, "fta_apres"),
                },
                {
                    label: "Puissance raccordement",
                    value: getMetricDisplay(analyse, "puissance_raccordement"),
                },
                {
                    label: "Date calcul",
                    value: formatDate(
                        analyse.createdAt ?? analyse.updatedAt,
                        "DD/MM/YYYY HH:mm"
                    ),
                },
            ],
            charts: mergeCharts(analyse, template),
            resultDetail: buildOptimTurpeResultDetailProps(analyse, meter),
        }
    })
}
